import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { normalizeGpsWaypoints, toGeoJsonLineString } from '../../services/gpsCoordinates';

const DEFAULT_CENTER = [9.0765, 7.3986];
const DEFAULT_ZOOM = 6;

function numberedIcon(n) {
  return L.divIcon({
    className: '',
    iconSize: [28, 38],
    iconAnchor: [14, 36],
    html: `
      <div style="position:relative;width:28px;height:38px;display:flex;justify-content:center;align-items:flex-start;">
        <div style="width:24px;height:24px;border-radius:9999px;background:#16a34a;border:2px solid #ffffff;box-shadow:0 8px 18px rgba(15,23,42,0.25);display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-weight:700;">${n}</div>
        <div style="position:absolute;bottom:0;width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:13px solid #16a34a;"></div>
      </div>
    `,
  });
}

function MapFocus({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], Math.max(map.getZoom(), 14), { animate: true });
      return;
    }
    map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [30, 30] });
  }, [map, points]);

  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 0);
    return () => clearTimeout(timer);
  }, [map]);

  return null;
}

function MapClickHandler({ onAdd }) {
  useMapEvents({
    click: (event) => onAdd({ lat: event.latlng.lat, lng: event.latlng.lng }),
  });
  return null;
}

function WaypointsMapModal({ open, points, onClose, onConfirm, onChange }) {
  const [draftPoints, setDraftPoints] = useState(points);

  useEffect(() => {
    setDraftPoints(points);
  }, [points]);

  if (!open) return null;

  const addPoint = (point) => setDraftPoints((prev) => [...prev, point]);
  const movePoint = (index, point) =>
    setDraftPoints((prev) => prev.map((p, i) => (i === index ? point : p)));
  const removePoint = (index) =>
    setDraftPoints((prev) => prev.filter((_, i) => i !== index));
  const clearAll = () => setDraftPoints([]);

  return (
    <div className="fixed inset-0 z-[2600] flex items-center justify-center bg-slate-950/65 px-4 py-6">
      <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-green-600">Capture route waypoints</p>
            <h3 className="mt-1 text-base font-semibold text-slate-900">Click the map to add points along the route</h3>
            <p className="mt-1 text-xs text-slate-500">
              Points are added in order. Drag a marker to adjust it, or remove one from the list on the right.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close waypoints picker"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid gap-4 px-6 py-5 lg:grid-cols-[1.45fr_0.85fr]">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <MapContainer
              center={draftPoints.length ? [draftPoints[0].lat, draftPoints[0].lng] : DEFAULT_CENTER}
              zoom={draftPoints.length ? DEFAULT_ZOOM : 6}
              scrollWheelZoom
              className="h-[420px] w-full"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {draftPoints.length >= 2 && (
                <Polyline positions={draftPoints.map((p) => [p.lat, p.lng])} pathOptions={{ color: '#16a34a', weight: 4 }} />
              )}
              {draftPoints.map((p, i) => (
                <Marker
                  key={i}
                  position={[p.lat, p.lng]}
                  icon={numberedIcon(i + 1)}
                  draggable
                  eventHandlers={{
                    dragend: (event) => {
                      const target = event.target.getLatLng();
                      movePoint(i, { lat: target.lat, lng: target.lng });
                    },
                  }}
                />
              ))}
              <MapFocus points={draftPoints} />
              <MapClickHandler onAdd={addPoint} />
            </MapContainer>
          </div>

          <div className="flex flex-col gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Waypoints ({draftPoints.length})
                </p>
                {draftPoints.length > 0 && (
                  <button type="button" onClick={clearAll} className="text-[11px] font-semibold text-red-600 hover:underline">
                    Clear all
                  </button>
                )}
              </div>
              <div className="mt-2 max-h-[280px] space-y-1.5 overflow-y-auto">
                {draftPoints.length === 0 && (
                  <p className="text-xs text-slate-400">No points yet — click the map to add the first one.</p>
                )}
                {draftPoints.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12px]">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-600 text-[10px] font-bold text-white">{i + 1}</span>
                    <span className="flex-1 truncate text-slate-700">{p.lat.toFixed(5)}, {p.lng.toFixed(5)}</span>
                    <button type="button" onClick={() => removePoint(i)} className="text-slate-400 hover:text-red-600">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-auto flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onConfirm(draftPoints)}
                disabled={draftPoints.length < 2}
                className="flex-1 rounded-full bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Use route ({draftPoints.length} pts)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GpsWaypointsField({
  label = 'GPS Waypoints',
  required = false,
  value,
  onChange,
  hint,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const points = normalizeGpsWaypoints(value);

  const handleConfirm = (draftPoints) => {
    onChange?.(toGeoJsonLineString(draftPoints));
    setOpen(false);
  };

  const summary = points.length >= 2
    ? `${points.length} waypoints captured`
    : points.length === 1
      ? '1 point captured — add at least one more to form a route'
      : '';

  return (
    <div className="space-y-1.5">
      <label className="block text-[12px] font-semibold text-gray-700">
        {label}{required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      <div className="flex items-center gap-2">
        <div className="flex-1 truncate rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 text-[13px] text-gray-700">
          {summary || <span className="text-gray-400">No route captured yet</span>}
        </div>
        <button
          type="button"
          onClick={() => !disabled && setOpen(true)}
          disabled={disabled}
          className="shrink-0 rounded-xl border border-dashed border-green-300 bg-green-50 px-4 py-3 text-[12px] font-semibold text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {points.length ? 'Edit route' : 'Capture route'}
        </button>
      </div>

      {hint && <p className="text-[11px] text-gray-400">{hint}</p>}

      <WaypointsMapModal
        open={open}
        points={points}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
